// Pure derived reads over AppState: no side effects and no store access, so views,
// the loader, and tests can call them against a plain state object (or a raw payload).

import { dayKey } from "../lib/datetime.js";

export function sessionsForDay(state, day) {
  return state.sessions.filter((s) => s.day === day).sort((a, b) => a.start.localeCompare(b.start));
}

export function availableDays(state) {
  return [...new Set(state.sessions.map((s) => s.day))].sort((a, b) => a - b);
}

export function allTags(state) {
  return [...new Set(state.sessions.flatMap((s) => s.tags))].sort((a, b) => a.localeCompare(b));
}

// Conference-timeline reads place "now" against the program. They take an explicit
// `now` (default `new Date()`) to stay pure and testable, and dayDateKeys takes a raw
// `sessions` array so the loader can call it against a fresh payload before the store
// is updated.
export function dayDateKeys(sessions) {
  const keys = new Map();
  for (const session of sessions) {
    if (!keys.has(session.day)) {
      keys.set(session.day, dayKey(session.start));
    }
  }
  return keys;
}

export function conferenceDayForDate(sessions, dateKey) {
  for (const [day, key] of dayDateKeys(sessions)) {
    if (key === dateKey) {
      return day;
    }
  }
  return null;
}

export function currentConferenceDay(state, now = new Date()) {
  return conferenceDayForDate(state.sessions, dayKey(now.toISOString()));
}

// Today STRICTLY after the last program day. Empty dataset counts as not over.
export function isConferenceOver(state, now = new Date()) {
  const keys = [...dayDateKeys(state.sessions).values()];
  if (keys.length === 0) {
    return false;
  }
  const lastDayKey = keys.reduce((a, b) => (a > b ? a : b));
  return dayKey(now.toISOString()) > lastDayKey;
}

// Past = already ENDED (keys off `end`). A session in progress is not past.
export function isSessionPast(session, now = new Date()) {
  return Date.parse(session.end) < now.getTime();
}

// Dim only sessions that ended WHILE the conference is still running; once it's over
// nothing is dimmed, since there is nothing left to attend.
export function isSessionDimmed(state, session, now = new Date()) {
  return !isConferenceOver(state, now) && isSessionPast(session, now);
}

export function isFavorite(state, id) {
  return state.favorites.has(id);
}

export function favoriteSessions(state) {
  return state.sessions
    .filter((s) => state.favorites.has(s.id))
    .sort((a, b) => a.day - b.day || a.start.localeCompare(b.start));
}

// Track filter is OR across the selected tracks; level/tag/search are AND. An empty
// facet is inactive (matches everything).
export function matchesFilters(session, filters) {
  if (filters.tracks.length > 0 && !filters.tracks.includes(session.trackId)) {
    return false;
  }
  if (filters.level && session.level !== filters.level) {
    return false;
  }
  if (filters.tag && !session.tags.includes(filters.tag)) {
    return false;
  }
  const query = filters.search.trim().toLowerCase();
  if (query && !session.title.toLowerCase().includes(query)) {
    return false;
  }
  return true;
}

// My Agenda ignores filters by design; only the schedule view narrows by them.
export function filteredSessionsForDay(state, day) {
  return sessionsForDay(state, day).filter((s) => matchesFilters(s, state.filters));
}

export function activeFilterCount(filters) {
  return filters.tracks.length + (filters.level ? 1 : 0) + (filters.tag ? 1 : 0) + (filters.search.trim() ? 1 : 0);
}
